import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, forkJoin, timer } from 'rxjs';
import { map, catchError, tap, retry} from 'rxjs/operators';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { environment } from 'apps/studio/src/environments/environment';

// Extraction state interfaces
export interface ExtractionState {
	status: 'idle' | 'extracting' | 'complete' | 'failed';
	results?: ExtractedData;
	error?: string;
}

export interface ExtractedData {
	voucher_info?: {
		date_sold?: string;
		store_name?: string;
		store_code?: string;
		sales_staff?: {
			name?: string;
			number?: string;
		};
	};
	customer?: {
		first_name?: string;
		surname?: string;
		mobile_1?: string;
		mobile_2?: string;
	};
	installation_address?: {
		street?: string;
		suburb?: string;
		city?: string;
		province?: string;
	};
	products?: Array<{
		article_number?: string;
		description?: string;
		quantity?: number;
		amount?: number;
	}>;
	totals?: {
		subtotal?: number;
		discount?: number;
		total?: number;
	};
}

export interface ExtractionApiResponse {
	success: boolean;
	data?: ExtractedData;
	error?: string;
}

export interface DocumentInput {
	id: string;
	base64: string;
}

export interface StoreOption {
	value: number | string;
	display: string;
}

// Form field types that can be auto-populated
export interface JobFormFields {
	store_purchased_from?: number | string | null;
	saleStaff?: string | null;
	quantity?: number | null;
	client_details?: {
		first_name?: string | null;
		surname?: string | null;
	};
	contact_details?: {
		cell_number?: string | null;
	};
}

@Injectable({ providedIn: 'root' })
export class DocumentExtractionService {
	private readonly http = inject(HttpClient);

	private readonly extractionState$ = new BehaviorSubject<ExtractionState>({ status: 'idle' });

	// Get the extraction API URL from environment or use default
	private get apiUrl(): string {
		return (environment as Record<string, unknown>)['document_extraction_api_url'] as string || 'http://localhost:3003';
	}

	/**
	 * Extract data from documents via the extraction API
	 * Processes documents sequentially and merges results
	 * Supports both images and PDFs (PDFs are converted to images on the server)
	 */
	extractDocuments(documents: DocumentInput[], schema?: object): Observable<ExtractionState> {
		if (!documents || documents.length === 0) {
			return of({ status: 'idle' } as ExtractionState);
		}

		// Set extracting state
		this.extractionState$.next({ status: 'extracting' });

		// Process each document (both images and PDFs are supported)
		const extractionRequests = documents.map(doc => this.extractSingleDocument(doc, schema));

		return forkJoin(extractionRequests).pipe(
			map(results => this.mergeExtractionResults(results)),
			tap(mergedData => {
				const state: ExtractionState = {
					status: 'complete',
					results: mergedData
				};
				this.extractionState$.next(state);
			}),
			map(() => this.extractionState$.value),
			catchError(error => {
				console.error('Document extraction failed:', error);
				const state: ExtractionState = {
					status: 'failed',
					error: error.message || 'Extraction failed'
				};
				this.extractionState$.next(state);
				return of(state);
			})
		);
	}

	/**
	 * Extract data from a single document
	 */
	private extractSingleDocument(doc: DocumentInput, schema?: object): Observable<ExtractedData | null> {
		const body: { document: string; schema?: object } = {
			document: doc.base64,
		};

		if (schema) {
			body.schema = schema;
		}

		return this.http.post<ExtractionApiResponse>(`${this.apiUrl}/extract`, body).pipe(
			// Retry up to 3 times with exponential backoff
			retry({
				count: 3,
				delay: (error, retryCount) => {
					console.warn(`Extraction retry ${retryCount} for document ${doc.id}:`, error.message);
					return timer(Math.pow(2, retryCount) * 1000); // 2s, 4s, 8s
				}
			}),
			map(response => {
				if (response.success && response.data) {
					return response.data;
				}
				console.warn(`Extraction returned no data for document ${doc.id}`);
				return null;
			}),
			catchError(error => {
				console.error(`Failed to extract document ${doc.id}:`, error);
				// Return null for failed documents - don't fail the entire batch
				return of(null);
			})
		);
	}

	/**
	 * Merge extraction results from multiple documents
	 * Later documents override earlier ones for conflicting fields
	 */
	private mergeExtractionResults(results: (ExtractedData | null)[]): ExtractedData {
		const merged: ExtractedData = {};

		for (const result of results) {
			if (!result) continue;

			// Merge voucher_info
			if (result.voucher_info) {
				merged.voucher_info = {
					...merged.voucher_info,
					...result.voucher_info,
					sales_staff: result.voucher_info.sales_staff
						? { ...merged.voucher_info?.sales_staff, ...result.voucher_info.sales_staff }
						: merged.voucher_info?.sales_staff
				};
			}

			// Merge customer
			if (result.customer) {
				merged.customer = {
					...merged.customer,
					...result.customer
				};
			}

			// Merge installation_address
			if (result.installation_address) {
				merged.installation_address = {
					...merged.installation_address,
					...result.installation_address
				};
			}

			// Merge products (concatenate arrays)
			if (result.products && result.products.length > 0) {
				merged.products = [...(merged.products || []), ...result.products];
			}

			// Merge totals
			if (result.totals) {
				merged.totals = {
					...merged.totals,
					...result.totals
				};
			}
		}

		return merged;
	}

	/**
	 * Get current extraction state as observable
	 */
	getExtractionState(): Observable<ExtractionState> {
		return this.extractionState$.asObservable();
	}

	/**
	 * Get current extraction state value
	 */
	getCurrentState(): ExtractionState {
		return this.extractionState$.value;
	}

	/**
	 * Reset extraction state to idle
	 */
	resetState(): void {
		this.extractionState$.next({ status: 'idle' });
	}

	/**
	 * Map extracted data to form field values
	 * Attempts to match store names against available stores
	 */
	mapExtractedDataToForm(data: ExtractedData, stores: StoreOption[]): Partial<JobFormFields> {
		const mappedValues: Partial<JobFormFields> = {};

		// Map store
		if (data.voucher_info?.store_name && stores.length > 0) {
			const matchedStore = this.findMatchingStore(data.voucher_info.store_name, data.voucher_info.store_code, stores);
			if (matchedStore) {
				mappedValues.store_purchased_from = matchedStore.value;
			}
		}

		// Map sales staff
		if (data.voucher_info?.sales_staff?.name) {
			mappedValues.saleStaff = data.voucher_info.sales_staff.name;
		}

		// Map quantity (sum from all products or use first product's quantity)
		if (data.products && data.products.length > 0) {
			const totalQuantity = data.products.reduce((sum, product) => {
				return sum + (product.quantity || 0);
			}, 0);

			if (totalQuantity > 0) {
				mappedValues.quantity = totalQuantity;
			}
		}

		// Map customer details if available
		if (data.customer) {
			if (data.customer.first_name || data.customer.surname) {
				mappedValues.client_details = {
					first_name: data.customer.first_name || null,
					surname: data.customer.surname || null
				};
			}

			if (data.customer.mobile_1) {
				mappedValues.contact_details = {
					cell_number: data.customer.mobile_1
				};
			}
		}

		return mappedValues;
	}

	/**
	 * Find a matching store from the stores list
	 * Uses fuzzy matching on store name and exact matching on store code
	 */
	private findMatchingStore(storeName: string, storeCode: string | undefined, stores: StoreOption[]): StoreOption | null {
		if (!storeName && !storeCode) {
			return null;
		}

		const normalizedSearchName = this.normalizeString(storeName || '');

		// First, try exact match on store code if available
		if (storeCode) {
			const codeMatch = stores.find(store => {
				const displayLower = store.display.toLowerCase();
				return displayLower.includes(storeCode.toLowerCase());
			});
			if (codeMatch) {
				return codeMatch;
			}
		}

		// Try exact name match
		const exactMatch = stores.find(store => {
			return this.normalizeString(store.display) === normalizedSearchName;
		});
		if (exactMatch) {
			return exactMatch;
		}

		// Try partial match - store name contains search name or vice versa
		const partialMatch = stores.find(store => {
			const normalizedDisplay = this.normalizeString(store.display);
			return normalizedDisplay.includes(normalizedSearchName) ||
				   normalizedSearchName.includes(normalizedDisplay);
		});
		if (partialMatch) {
			return partialMatch;
		}

		// Try word-based matching - check if key words match
		const searchWords = normalizedSearchName.split(/\s+/).filter(w => w.length > 2);
		if (searchWords.length > 0) {
			const wordMatch = stores.find(store => {
				const displayWords = this.normalizeString(store.display).split(/\s+/);
				// Check if at least 50% of search words are found in display
				const matchCount = searchWords.filter(sw => displayWords.some(dw => dw.includes(sw) || sw.includes(dw))).length;
				return matchCount >= Math.ceil(searchWords.length / 2);
			});
			if (wordMatch) {
				return wordMatch;
			}
		}

		return null;
	}

	/**
	 * Normalize a string for comparison
	 */
	private normalizeString(str: string): string {
		return str
			.toLowerCase()
			.replace(/[^\w\s]/g, '') // Remove special characters
			.replace(/\s+/g, ' ')    // Normalize whitespace
			.trim();
	}
}
